// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
/**
 * UN CHAMP TÉLÉPHONE : l'indicatif pays est SÉPARÉ du numéro et pré-saisi à +216 (Tunisie) ;
 * une liste déroulante permet d'en changer (Ines, 21/10/2026 : « dans toute l'appli »).
 *
 *     <ChampTelephone value={tel} onChange={setTel} ariaLabel="Téléphone" />
 *
 * La valeur échangée reste UNE chaîne, `+216 98 433 054` : les formulaires, les API et la base
 * n'ont pas changé. Vide tant qu'aucun chiffre n'est saisi (l'indicatif seul n'est pas un numéro :
 * un champ facultatif reste vide, un champ obligatoire reste refusé).
 *
 * « Autre indicatif » (valeur '') : un numéro dont l'indicatif n'est pas dans la liste se saisit en
 * entier dans la case du numéro, avec son « + ». Une ancienne valeur sans « + » (`73 462 717`) est
 * lue comme tunisienne ; une ancienne valeur étrangère hors liste est montrée telle quelle.
 */
import React from 'react';

export const INDICATIFS_TEL: readonly { code: string; pays: string; iso: string }[] = [
  { code: '+216', pays: 'Tunisie', iso: 'TN' }, { code: '+33', pays: 'France', iso: 'FR' }, { code: '+213', pays: 'Algérie', iso: 'DZ' },
  { code: '+218', pays: 'Libye', iso: 'LY' }, { code: '+212', pays: 'Maroc', iso: 'MA' }, { code: '+222', pays: 'Mauritanie', iso: 'MR' },
  { code: '+20', pays: 'Égypte', iso: 'EG' }, { code: '+39', pays: 'Italie', iso: 'IT' }, { code: '+49', pays: 'Allemagne', iso: 'DE' },
  { code: '+32', pays: 'Belgique', iso: 'BE' }, { code: '+41', pays: 'Suisse', iso: 'CH' }, { code: '+34', pays: 'Espagne', iso: 'ES' },
  { code: '+44', pays: 'Royaume-Uni', iso: 'GB' }, { code: '+1', pays: 'Amérique du Nord', iso: 'US/CA' }, { code: '+90', pays: 'Turquie', iso: 'TR' },
  { code: '+966', pays: 'Arabie saoudite', iso: 'SA' }, { code: '+971', pays: 'Émirats arabes unis', iso: 'AE' }, { code: '+974', pays: 'Qatar', iso: 'QA' },
  { code: '+221', pays: 'Sénégal', iso: 'SN' }, { code: '+225', pays: 'Côte d’Ivoire', iso: 'CI' },
];

const PAR_LONGUEUR = [...INDICATIFS_TEL].sort((a, b) => b.code.length - a.code.length);

/** Sépare une valeur saisie en indicatif / numéro. `ind === ''` = « autre indicatif ». */
export function separerTelephone(valeur: string): { ind: string; nat: string } {
  const brut = (valeur || '').replace(/^\s+/, '');
  if (!brut) return { ind: '+216', nat: '' };
  const intl = brut.startsWith('00') ? `+${brut.slice(2)}` : brut;
  if (!intl.startsWith('+')) return { ind: '+216', nat: brut };
  const chiffres = intl.slice(1).replace(/\s+/g, '');
  const trouve = PAR_LONGUEUR.find((x) => chiffres.startsWith(x.code.slice(1)));
  if (!trouve) return { ind: '', nat: intl };
  // On saute l'indicatif ET les espaces qui le suivent, sans toucher au reste de la mise en forme.
  let i = 1, vus = 0;
  while (i < intl.length && vus < trouve.code.length - 1) { if (/\d/.test(intl[i])) vus += 1; i += 1; }
  return { ind: trouve.code, nat: intl.slice(i).replace(/^\s+/, '') };
}

export const composerTelephone = (ind: string, nat: string) => (nat.trim() ? `${ind ? `${ind} ` : ''}${nat}` : '');

export function ChampTelephone({
  value, onChange, ariaLabel, id, required, disabled, style, selectStyle, inputRef, placeholder, onBlur, describedBy, nomIndicatif = 'Indicatif',
}: {
  value: string;
  onChange: (valeur: string) => void;
  ariaLabel?: string;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  /** Style du champ du numéro (et, à défaut de `selectStyle`, de la liste). */
  style?: React.CSSProperties;
  selectStyle?: React.CSSProperties;
  inputRef?: React.Ref<HTMLInputElement>;
  placeholder?: string;
  onBlur?: () => void;
  describedBy?: string;
  /** Nom accessible de la liste des indicatifs (à traduire par l'appelant si besoin). */
  nomIndicatif?: string;
}) {
  const lu = separerTelephone(value);
  // Tant que le champ est vide, l'indicatif choisi n'existe nulle part ailleurs que dans l'état.
  const [choisi, setChoisi] = React.useState(lu.ind);
  const ind = value.trim() ? lu.ind : choisi;
  const nat = value.trim() ? lu.nat : '';
  const autre = ind === '';

  const saisir = (brut: string) => {
    let texte = brut.replace(/[^\d\s.\-()/+]/g, '');
    // « Autre indicatif » : le « + » est ce qui dit que le numéro est international ; sans lui,
    // la valeur serait relue comme tunisienne.
    if (autre && texte.trim() && !/^\s*(\+|00)/.test(texte)) texte = `+${texte.trim()}`;
    // Un numéro collé en entier (« +33 6 12… », « 00216… ») redécoupe l'indicatif.
    if (/^\s*(\+|00)/.test(texte)) {
      const p = separerTelephone(texte);
      setChoisi(p.ind);
      onChange(composerTelephone(p.ind, p.nat));
      return;
    }
    onChange(composerTelephone(ind, texte.replace(/\+/g, '')));
  };
  const changerIndicatif = (code: string) => {
    setChoisi(code);
    // « Autre » repart d'une case vide : le numéro déjà saisi n'a plus son indicatif, et sans « + »
    // il serait relu comme tunisien.
    onChange(code === '' ? '' : composerTelephone(code, nat));
  };

  const champ: React.CSSProperties = { ...style, flex: '1 1 0', minWidth: 0, width: 'auto' };
  const liste: React.CSSProperties = { ...(selectStyle || style), flex: '0 0 auto', width: 'auto', fontFamily: 'var(--font-mono)' };
  return (
    <div dir="ltr" style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
      <select aria-label={nomIndicatif} value={ind} disabled={disabled} style={liste} onChange={(e) => changerIndicatif(e.target.value)}>
        {INDICATIFS_TEL.map((x) => <option key={x.code} value={x.code} title={x.pays}>{x.code} {x.iso}</option>)}
        <option value="">Autre (+…)</option>
      </select>
      <input id={id} ref={inputRef} type="tel" inputMode="tel" autoComplete="tel-national" required={required} disabled={disabled}
        aria-label={ariaLabel} aria-describedby={describedBy} value={nat} onBlur={onBlur}
        placeholder={placeholder ?? (autre ? '+49 170 123 4567' : ind === '+216' ? '98 433 054' : '')}
        onChange={(e) => saisir(e.target.value)} style={{ ...champ, fontFamily: 'var(--font-mono)' }} />
    </div>
  );
}
